import { createFileRoute, Link } from "@tanstack/react-router"
import { BellOff, BookmarkMinus } from "lucide-react"
import { toast } from "sonner"
import type { Id } from "../../convex/_generated/dataModel"
import { Skeleton } from "@/components/ui/skeleton"
import { Chip } from "@/components/chips"
import { EmptyState } from "@/components/empty-state"
import { EntityRow } from "@/components/entity-row"
import { jobWorkModeLabel } from "@/lib/employment"
import { errorMessage } from "@/lib/errors"
import { formatSalary } from "@/lib/salary"
import {
  serializeJobsBrowseSearch,
  ShareJobButton,
  useJobAlerts,
  useSavedJobsList,
} from "@/features/jobs"

export const Route = createFileRoute("/candidate/saved")({
  component: SeekerSaved,
})

function SeekerSaved() {
  const { items, unsave } = useSavedJobsList()
  const { alerts, remove: removeAlert } = useJobAlerts()

  const handleUnsave = async (jobId: Id<"jobs">) => {
    try {
      await unsave(jobId)
      toast.success("Removed from saved jobs")
    } catch (e) {
      toast.error(errorMessage(e, "Could not remove"))
    }
  }

  const handleRemoveAlert = async (alertId: Id<"candidateJobAlerts">) => {
    try {
      await removeAlert(alertId)
      toast.success("Alert removed")
    } catch (e) {
      toast.error(errorMessage(e, "Could not remove alert"))
    }
  }

  return (
    <div>
      <h1 className="mkt-page-title">Saved</h1>
      <p className="mkt-page-sub">
        Roles you bookmarked and the searches you're alerted about.
      </p>

      {items === undefined ? (
        <div className="mt-6 space-y-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          className="mt-6"
          title="No saved jobs yet."
          description="Tap the bookmark on any role to keep it here."
          action={{ label: "Browse open roles", to: "/jobs/browse" }}
        />
      ) : (
        <div className="mt-4">
          {items.map((item) => {
            const salary = formatSalary(item)
            return (
              <EntityRow
                key={item.savedId}
                to="/jobs/$jobId"
                params={{ jobId: item.jobId }}
                mark={{
                  name: item.company?.companyName ?? item.employerName,
                  imageUrl: item.company?.companyLogoUrl,
                }}
                title={item.title}
                chips={
                  <>
                    {!item.jobOpen && <Chip>Closed</Chip>}
                    <Chip emphasis={item.jobOpen}>{item.employmentType}</Chip>
                    {item.workMode && <Chip>{jobWorkModeLabel(item.workMode)}</Chip>}
                    {salary && <Chip>{salary}</Chip>}
                  </>
                }
                meta={`${item.employerName} · ${item.location}`}
                regions={item.remoteRegions ?? undefined}
                tags={{ items: item.skills, max: 3 }}
                className={item.jobOpen ? undefined : "opacity-60"}
                action={
                  <div className="flex items-center gap-0.5 self-start">
                    {item.jobOpen && (
                      <ShareJobButton
                        jobId={item.jobId}
                        title={item.title}
                        companyName={item.company?.companyName ?? item.employerName}
                        variant="icon"
                      />
                    )}
                    <button
                      type="button"
                      className="mkt-row-action self-start"
                      aria-label={`Remove ${item.title} from saved jobs`}
                      onClick={() => void handleUnsave(item.jobId)}
                    >
                      <BookmarkMinus className="size-4" aria-hidden />
                    </button>
                  </div>
                }
              />
            )
          })}
        </div>
      )}

      <h2 className="mkt-page-title mt-10 text-xl">Job alerts</h2>
      <p className="mkt-page-sub">
        A daily email + notification when new roles match a saved search.
      </p>
      {alerts === undefined ? (
        <Skeleton className="mt-4 h-14 w-full" />
      ) : alerts.length === 0 ? (
        <p className="mt-4 text-sm text-[color:var(--wf-mute)]">
          None yet — filter the{" "}
          <Link
            to="/jobs/browse"
            className="font-bold text-[color:var(--wf-lime-text)] hover:underline"
          >
            jobs board
          </Link>{" "}
          and choose “Alert me about new roles like this”.
        </p>
      ) : (
        <ul className="mt-2 list-none p-0">
          {alerts.map((alert) => (
            <li key={alert._id} className="mkt-page-row">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <Link
                  to="/jobs/browse"
                  search={
                    serializeJobsBrowseSearch({
                      q: alert.q ?? "",
                      type: alert.employmentType ?? "all",
                      regions: (alert.regions ?? []).join(","),
                      sort: "newest",
                    }) as never
                  }
                  className="mkt-page-row-title truncate"
                >
                  {alert.name}
                </Link>
                <button
                  type="button"
                  className="mkt-row-action inline-flex items-center gap-1.5"
                  onClick={() => void handleRemoveAlert(alert._id)}
                >
                  <BellOff className="size-4" aria-hidden />
                  Remove
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
